<template>
  <SettingsForm :initial-values="initialValues">
    <SettingsFormItem :title="$t('common.country')">
      <SelectInputField
        name="country"
        :placeholder="$t('common.country_placeholder')"
        :options="countriesStore.options"
        @input="(value) => setCountry(value)"
      />
    </SettingsFormItem>
    <SettingsFormItem :title="$t('common.preferred_language')">
      <SelectInputField
        name="language"
        :placeholder="$t('common.language_placeholder')"
        :options="languages"
        @input="(value) => setLanguage(value)"
      />
    </SettingsFormItem>
    <SettingsFormItem :title="$t('common.city')">
      <SelectInputField
        v-if="countriesStore.isCurrentCountryLithuania"
        name="city"
        :placeholder="$t('common.city_placeholder')"
        :options="citiesStore.options"
        @input="(value) => setCity(value)"
      />
      <TextInputField
        v-else
        name="city"
        :placeholder="$t('common.city_placeholder')"
        @input="(event) => setValueDebounced(event.target.value)"
      />
    </SettingsFormItem>
    <SettingsFormItem
      :title="$t('common.timezone')"
      :description="
        $t(
          'common.we_will_remind_you_about_the_courses_according_to_the_set_time_zone',
        )
      "
    >
      <SelectInputField
        name="timezone"
        :placeholder="$t('common.timezone_placeholder')"
        :options="timezonesStore.options"
        @input="(value) => setTimezone(value)"
      />
    </SettingsFormItem>
  </SettingsForm>
</template>

<script setup>
import i18n from "@/i18n";
import { computed, defineEmits, defineProps, watch } from "vue";
import { useCountriesStore } from "@/store/modules/countries.store";
import { useTimezonesStore } from "@/store/modules/timezones.store";
import { useCitiesStore } from "@/store/modules/cities.store";
import { useLocaleStore } from "@/store/modules/locale.store";
import { useSettingsStore } from "@/store/modules/settings.store";
import SettingsFormItem from "@/components/form/dashboard/components/SettingsFormItem.vue";
import SettingsForm from "@/components/form/dashboard/components/SettingsForm.vue";
import SelectInputField from "../SelectInputField.vue";
import TextInputField from "@/components/form/TextInputField.vue";
import debounce from "lodash.debounce";

const countriesStore = useCountriesStore();
const timezonesStore = useTimezonesStore();
const citiesStore = useCitiesStore();
const languagesStore = useLocaleStore();
const settingsStore = useSettingsStore();

const emit = defineEmits(["submit"]);
const props = defineProps({ initialValues: Object, loading: Boolean });

languagesStore.request();
countriesStore.request();
timezonesStore.request();
countriesStore.setCurrent(props.initialValues.country || null);

const languages = computed(() =>
  languagesStore.languages.map((language) => ({
    name: language.title_native,
    value: language.tag,
  })),
);

const setValue = (key, value, extraAction) => {
  if (value === props.initialValues[key]) {
    return;
  }

  if (extraAction) {
    extraAction(value);
  }

  emit("submit", { [key]: value });
};
const setValueDebounced = debounce((value) => setValue("city", value), 1000);
const setCountry = (value) => {
  setValue("country", value, countriesStore.setCurrent);
};
const setCity = (value) => {
  setValue("city", value);
};
const setTimezone = (value) => {
  setValue("timezone", value);
};
const setLanguage = (value) => {
  if (!value || props.initialValues.language === value) {
    return;
  }

  languagesStore.update(value);
};

watch(
  () => i18n.global.locale,
  () => settingsStore.request(true),
);
watch(() => countriesStore.currentCountryKey, citiesStore.request);
</script>

<style lang="scss" scoped></style>
